import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useAutoLogout } from '@/hooks/useAutoLogout';
import { playClickSound } from '@/utils/soundUtils';

function formatTimeRemaining(ms: number, format: 'minutes' | 'seconds'): string {
  if (format === 'seconds') {
    return `${Math.ceil(ms / 1000)}s`;
  }
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

export default function AutoLogoutWarning() {
  const { timeRemaining, isWarningVisible, resetTimer, format } = useAutoLogout();

  if (!isWarningVisible || timeRemaining <= 0) {
    return null;
  }

  const handleStayActive = () => {
    playClickSound();
    resetTimer();
  };

  return (
    <div
      className="fixed top-4 right-4 z-[60] transform transition-all duration-300 ease-in-out"
      style={{ maxWidth: 'min(28rem, calc(100vw - 2rem))' }}
    >
      <div
        className={`
          bg-gradient-to-br from-red-500 via-red-600 to-rose-700
          border-2 border-red-400/50
          rounded-lg shadow-2xl p-4
          backdrop-blur-md
          animate-pulse
        `}
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-bold text-base">
                Auto-Logout Warning
              </h3>
            </div>
            <p className="text-white/90 text-sm mb-3">
              Logging out in: <span className="font-bold text-lg text-yellow-200">{formatTimeRemaining(timeRemaining, format)}</span>
            </p>
            <button
              onClick={handleStayActive}
              className="
                w-full
                px-4 py-2
                bg-white/20 hover:bg-white/30
                border border-white/40
                rounded-lg
                text-white font-semibold text-sm
                transition-all duration-200
                active:scale-95
                backdrop-blur-sm
              "
            >
              Stay Active
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
